import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Star, Loader2, Send, User, Lightbulb, GraduationCap, Trash2, StickyNote, Check } from "lucide-react";

function Bar({ label, value, tone }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1"><span className="text-muted-foreground">{label}</span><span className="font-semibold">{v}%</span></div>
      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${tone === "accent" ? "bg-accent" : "bg-primary"}`} style={{ width: `${v}%` }} /></div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    accepted: { label: "Tackat ja", cls: "bg-primary/10 text-primary border-primary/25" },
    declined: { label: "Tackat nej", cls: "bg-destructive/10 text-destructive border-destructive/25" },
    pending: { label: "Väntar på svar", cls: "bg-amber-100 text-amber-700 border-amber-300" },
    none: { label: "Ej kontaktad", cls: "bg-muted text-muted-foreground border-border" },
  };
  const s = map[status] || map.none;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`} data-testid="saved-status-badge">{s.label}</span>;
}

function SavedCard({ item, status, contacted, onContacted, onRemove }) {
  const m = item.match || {};
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [savedNote, setSavedNote] = useState(item.note || "");
  const [savingNote, setSavingNote] = useState(false);
  const already = contacted.includes(item.candidate_id);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/employer/shortlist/${item.candidate_id}/note`, { note });
      setSavedNote(note);
      toast.success("Notering sparad");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };

  const sendRequest = async () => {
    setSending(true);
    try {
      await api.post("/employer/contact-requests", { candidate_id: item.candidate_id, search_id: item.search_id, message });
      toast.success("Kontaktförfrågan skickad");
      setOpen(false);
      onContacted(item.candidate_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Card className="rounded-2xl border-border p-5" data-testid={`saved-${item.candidate_id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><User className="h-4.5 w-4.5" /></div>
          <div className="min-w-0">
            <div className="font-mono text-sm font-semibold text-primary">{item.candidate_id}</div>
            {item.role_title && <div className="text-xs text-muted-foreground truncate">Sparad för: {item.role_title}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={status} />
          <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => onRemove(item.candidate_id)} data-testid={`remove-saved-${item.candidate_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {(typeof m.role_match === "number" || typeof m.character_match === "number") && (
        <div className="grid grid-cols-2 gap-4 mb-3">
          <Bar label="Rollmatchning" value={m.role_match} />
          <Bar label="Karaktärsmatchning" value={m.character_match} tone="accent" />
        </div>
      )}
      {m.verdict && <p className="text-sm text-muted-foreground mb-3">{m.verdict}</p>}
      {(m.tips || []).length > 0 && (
        <div className="rounded-xl bg-primary/[0.05] border border-primary/15 p-3 mb-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-primary mb-1"><Lightbulb className="h-4 w-4" /> Tips</div>
          {m.tips.map((t, i) => <div key={i} className="text-sm text-foreground/80">• {t}</div>)}
        </div>
      )}
      {(m.education_suggestions || []).length > 0 && (
        <div className="rounded-xl bg-accent/[0.07] border border-accent/20 p-3 mb-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-accent mb-1"><GraduationCap className="h-4 w-4" /> Utbildningar som stärker matchningen</div>
          {m.education_suggestions.map((e, i) => <div key={i} className="text-sm text-foreground/80">• {e}</div>)}
        </div>
      )}
      <div className="mb-3">
        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-1"><StickyNote className="h-3.5 w-3.5" /> Intern notering (endast du ser den)</label>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="T.ex. 'Stark på karaktär, ring om två veckor'…" data-testid={`note-input-${item.candidate_id}`} />
        {note !== savedNote && (
          <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={saveNote} disabled={savingNote} data-testid={`save-note-${item.candidate_id}`}>
            {savingNote ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Spara notering
          </Button>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="rounded-full" disabled={already} data-testid={`saved-contact-btn-${item.candidate_id}`}>
            {already ? "Förfrågan skickad" : <><Send className="h-4 w-4 mr-1.5" /> Skicka kontaktförfrågan</>}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Kontakta {item.candidate_id}</DialogTitle></DialogHeader>
          <DialogDescription>Kandidaten ser bara ditt företagsnamn och ditt meddelande – och väljer själv om hen vill svara.</DialogDescription>
          <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Skriv en kort hälsning…" data-testid="saved-contact-message" />
          <DialogFooter>
            <Button className="rounded-full" onClick={sendRequest} disabled={sending} data-testid="saved-contact-send">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Skicka förfrågan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function ForetagSparade() {
  const [list, setList] = useState(null);
  const [contacted, setContacted] = useState([]);
  const [statusMap, setStatusMap] = useState({});

  const load = () => api.get("/employer/shortlist").then((r) => setList(r.data)).catch(() => setList([]));
  const loadContacted = () => api.get("/employer/contact-requests").then((r) => {
    const reqs = r.data || [];
    const map = {};
    reqs.forEach((c) => { if (!map[c.candidate_id]) map[c.candidate_id] = c.status; }); // list is sorted desc → first = latest
    setStatusMap(map);
    setContacted(reqs.filter((c) => c.status !== "declined").map((c) => c.candidate_id));
  }).catch(() => {});
  useEffect(() => { load(); loadContacted(); }, []);

  const remove = async (cid) => {
    try { await api.delete(`/employer/shortlist/${cid}`); setList((l) => l.filter((x) => x.candidate_id !== cid)); toast.success("Borttagen"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-head text-3xl font-bold flex items-center gap-2"><Star className="h-7 w-7 text-primary" /> Sparade kandidater</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Din shortlist inför beslut. Endast HAMN-id sparas – aldrig namn, ålder eller kön. Skicka en kontaktförfrågan när du är redo.</p>
      </div>
      {list === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-border p-10 text-center" data-testid="shortlist-empty">
          <Star className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Inga sparade kandidater ännu. Klicka på "Spara" på en matchning under Sök kandidater.</p>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="shortlist">
          {list.map((item) => (
            <SavedCard key={item.candidate_id} item={item} status={statusMap[item.candidate_id] || "none"} contacted={contacted} onContacted={(cid) => { setContacted((c) => [...c, cid]); setStatusMap((m) => ({ ...m, [cid]: "pending" })); }} onRemove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}
